/**
 * Table chrome — applied to every <table class="t"> rendered into the view.
 *
 *   • Auto-fit columns (table-layout stays `auto`). Wide text columns carry the
 *     `cell-clip` class and truncate with an ellipsis; the full value shows on
 *     hover via the title attribute.
 *   • Show / hide columns popover, keyed by each header's `data-col` identifier
 *     (robust against column reordering) and persisted per section, with
 *     sensible per-section defaults the first time a section is opened.
 *   • Sortable column headers (ascending → descending → cleared), persisted per
 *     section.
 *
 * Single entry point: `setupAllTables(viewRoot, sectionKey)`.
 */
import { escapeHtml } from "../lib/dom";
import { icon } from "./icon";

/* ------------------------------------------------------------------ */
/*  Per-section default-hidden columns (by data-col key)                */
/*  These apply only until the user customizes a section's columns.     */
/* ------------------------------------------------------------------ */
const DEFAULT_HIDDEN: Record<string, string[]> = {
  // Unit FMV (the per-unit asking price + 🔒 lock indicator) is intentionally
  // visible by default; cost-basis columns stay hidden (nice at card shows).
  cards:  ["Game", "Class", "Unit Basis", "Total Basis", "G/L", "Acq Date", "Source"],
  sealed: ["Game", "Unit Basis", "Total Basis", "G/L", "Acq Date", "Source"],
};

/* ------------------------------------------------------------------ */
/*  Cell title (hover tooltip for clipped text)                         */
/* ------------------------------------------------------------------ */
function applyCellTitles(tableEl: HTMLTableElement): void {
  const cells = tableEl.querySelectorAll<HTMLTableCellElement>(":scope > tbody > tr > td");
  cells.forEach((cell) => {
    if (cell.hasAttribute("title")) return;
    if (cell.querySelector("button, input, select, textarea, a")) return;
    const txt = (cell.textContent || "").trim();
    if (txt && txt !== "—") cell.setAttribute("title", txt);
  });
}

/* ------------------------------------------------------------------ */
/*  Column visibility (keyed by header data-col)                        */
/* ------------------------------------------------------------------ */
const COL_VIS_KEY = "enigma_colvis_v2";

/** Stored hidden keys for a section, or null when the user hasn't customized
 *  it yet (so defaults should apply). */
function loadStoredHidden(section: string): string[] | null {
  try {
    const all = JSON.parse(localStorage.getItem(COL_VIS_KEY) || "{}");
    return Array.isArray(all[section]) ? all[section] : null;
  } catch { return null; }
}
function saveStoredHidden(section: string, hidden: string[]): void {
  let all: Record<string, string[]> = {};
  try { all = JSON.parse(localStorage.getItem(COL_VIS_KEY) || "{}"); } catch { /* ignore */ }
  all[section] = hidden.slice();
  localStorage.setItem(COL_VIS_KEY, JSON.stringify(all));
}
function clearStoredHidden(section: string): void {
  let all: Record<string, string[]> = {};
  try { all = JSON.parse(localStorage.getItem(COL_VIS_KEY) || "{}"); } catch { /* ignore */ }
  delete all[section];
  localStorage.setItem(COL_VIS_KEY, JSON.stringify(all));
}
/** Effective hidden keys: the user's stored choice if present, else defaults. */
function effectiveHidden(section: string): string[] {
  const stored = loadStoredHidden(section);
  return stored !== null ? stored : (DEFAULT_HIDDEN[section] || []);
}

function applyHiddenColumns(tableEl: HTMLTableElement, section: string): void {
  const hidden = new Set(effectiveHidden(section));
  const ths = Array.from(tableEl.querySelectorAll<HTMLTableCellElement>(":scope > thead > tr > th"));
  const hideIdx = new Set<number>();
  ths.forEach((th, i) => {
    const key = th.getAttribute("data-col") || "";
    if (key && hidden.has(key)) hideIdx.add(i);
  });
  const trs = tableEl.querySelectorAll<HTMLTableRowElement>(":scope > thead > tr, :scope > tbody > tr");
  trs.forEach((tr) => {
    // Skip the single-cell "no rows" placeholder (colspan row) — it has one
    // child that should always stay visible.
    if (tr.children.length === 1 && tr.children[0].hasAttribute("colspan")) return;
    Array.from(tr.children).forEach((cell, i) => {
      (cell as HTMLElement).style.display = hideIdx.has(i) ? "none" : "";
    });
  });
}

function injectColumnsControl(tableEl: HTMLTableElement, section: string): void {
  const panel = tableEl.parentElement;
  if (!panel || !panel.parentElement) return;

  const viewRoot = document.getElementById("view");
  const host = viewRoot?.querySelector<HTMLElement>("[data-cols-host]") ?? null;

  if (host && host.querySelector(`[data-cols-toolbar="${section}"]`)) return;
  if (
    !host &&
    panel.previousElementSibling &&
    panel.previousElementSibling.getAttribute("data-cols-toolbar") === section
  ) return;

  const ths = Array.from(tableEl.querySelectorAll<HTMLTableCellElement>(":scope > thead > tr > th"));
  // Only columns with a data-col identifier are user-toggleable (the leading
  // thumbnail column has none and stays visible).
  const cols = ths
    .map(th => th.getAttribute("data-col") || "")
    .filter(Boolean)
    .map(key => ({ key, label: key }));
  if (!cols.length) return;
  const hidden = new Set(effectiveHidden(section));

  const bar = document.createElement("div");
  bar.setAttribute("data-cols-toolbar", section);
  bar.className = `flex items-center gap-2 relative${host ? "" : " justify-start mb-2"}`;
  bar.innerHTML = `
    <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" data-cols-toggle title="Show / hide columns">${icon("columns", { size: 13 })} Columns</button>
    <span class="text-xs text-[var(--muted)]" data-cols-status>${hidden.size ? `${hidden.size} hidden` : ""}</span>
    <div class="hidden absolute left-0 top-full mt-1 panel p-2 z-30 min-w-[240px] max-h-[60vh] overflow-y-auto shadow-2xl" data-cols-popover>
      <div class="text-xs text-[var(--muted)] mb-1 px-1">Show / hide columns</div>
      ${cols.map(c => `
        <label class="flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer hover:bg-[var(--panel-2)]">
          <input type="checkbox" data-col-key="${escapeHtml(c.key)}" ${hidden.has(c.key) ? "" : "checked"}>
          <span>${escapeHtml(c.label)}</span>
        </label>
      `).join("")}
      <div class="border-t border-[var(--border)] mt-1 pt-1 flex gap-1">
        <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs flex-1" data-cols-default>Reset to default</button>
        <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs flex-1" data-cols-all>Show all</button>
      </div>
    </div>
  `;
  if (host) {
    host.innerHTML = "";
    host.appendChild(bar);
  } else {
    panel.parentElement.insertBefore(bar, panel);
  }

  const popover = bar.querySelector<HTMLElement>("[data-cols-popover]")!;
  const toggleBtn = bar.querySelector<HTMLElement>("[data-cols-toggle]")!;
  let dismissHandler: ((e: MouseEvent) => void) | null = null;
  const closePopover = () => {
    popover.classList.add("hidden");
    if (dismissHandler) {
      document.removeEventListener("mousedown", dismissHandler);
      dismissHandler = null;
    }
  };
  const openPopover = () => {
    popover.classList.remove("hidden");
    dismissHandler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (popover.contains(target) || toggleBtn.contains(target)) return;
      closePopover();
    };
    setTimeout(() => document.addEventListener("mousedown", dismissHandler!), 0);
  };
  toggleBtn.addEventListener("click", () => {
    if (popover.classList.contains("hidden")) openPopover();
    else closePopover();
  });
  bar.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-col-key]').forEach((cb) => {
    cb.addEventListener("change", () => {
      const key = cb.getAttribute("data-col-key") || "";
      toggleColumnVisibility(section, key, bar);
    });
  });
  bar.querySelector<HTMLElement>("[data-cols-default]")
    ?.addEventListener("click", () => resetColumnsToDefault(section, bar));
  bar.querySelector<HTMLElement>("[data-cols-all]")
    ?.addEventListener("click", () => showAllColumns(section, bar));
}

function reapplyAll(section: string): void {
  const root = document.getElementById("view");
  if (!root) return;
  root.querySelectorAll<HTMLTableElement>("table.t").forEach(tbl => applyHiddenColumns(tbl, section));
}
function updateStatus(bar: HTMLElement, section: string): void {
  const count = effectiveHidden(section).length;
  const status = bar.querySelector<HTMLElement>("[data-cols-status]");
  if (status) status.textContent = count ? `${count} hidden` : "";
}

function toggleColumnVisibility(section: string, key: string, bar: HTMLElement): void {
  if (!key) return;
  const cur = new Set(effectiveHidden(section));
  if (cur.has(key)) cur.delete(key); else cur.add(key);
  saveStoredHidden(section, [...cur]);
  reapplyAll(section);
  updateStatus(bar, section);
}
function resetColumnsToDefault(section: string, bar: HTMLElement): void {
  clearStoredHidden(section); // fall back to defaults
  reapplyAll(section);
  const hidden = new Set(effectiveHidden(section));
  bar.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-col-key]').forEach(cb => {
    cb.checked = !hidden.has(cb.getAttribute("data-col-key") || "");
  });
  updateStatus(bar, section);
}
function showAllColumns(section: string, bar: HTMLElement): void {
  saveStoredHidden(section, []);
  reapplyAll(section);
  bar.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-col-key]').forEach(cb => cb.checked = true);
  updateStatus(bar, section);
}

/* ------------------------------------------------------------------ */
/*  Column sort                                                         */
/* ------------------------------------------------------------------ */
const SORT_KEY = "enigma_sort_v2"; // v2: keyed by data-col name, not column index

interface SortRecord { key: string; dir: "asc" | "desc"; }

/** Sections whose views sort the FULL dataset before pagination (via
 *  sectionSort + sortRowsForView below). setupAllTables skips the DOM-level
 *  page sort for these so the two never disagree. */
const DATA_SORTED_SECTIONS = new Set(["cards", "sealed"]);

function loadSort(section: string): SortRecord | null {
  try {
    const all = JSON.parse(localStorage.getItem(SORT_KEY) || "{}");
    const v = all[section];
    return v && typeof v.key === "string" ? v : null;
  } catch { return null; }
}

/** Public: the section's current sort (or null). */
export function sectionSort(section: string): SortRecord | null { return loadSort(section); }

/** Public: sort a view's full data array (before pagination) so page 1 shows
 *  the true top. Numbers compare numerically; everything else localeCompare
 *  (numeric-aware). Blank values sort last (first on desc). */
export function sortRowsForView<T>(rows: T[], dir: "asc" | "desc", accessor: (row: T) => any): T[] {
  const sorted = rows.slice().sort((a, b) => {
    const av = accessor(a), bv = accessor(b);
    const aEmpty = av == null || av === "";
    const bEmpty = bv == null || bv === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (typeof av === "number" && typeof bv === "number") return av - bv;
    return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
  });
  if (dir === "desc") sorted.reverse();
  return sorted;
}
function saveSort(section: string, state: SortRecord | null): void {
  let all: Record<string, SortRecord | undefined> = {};
  try { all = JSON.parse(localStorage.getItem(SORT_KEY) || "{}"); } catch { /* ignore */ }
  if (state) all[section] = state;
  else delete all[section];
  localStorage.setItem(SORT_KEY, JSON.stringify(all));
}

function parseSortNum(s: string | null | undefined): number | null {
  if (s == null) return null;
  const t = String(s).trim();
  if (!t || t === "—") return null;
  const isNeg = /^\(.*\)$/.test(t);
  const cleaned = t.replace(/[\$,()\s%]/g, "").replace(/^\+/, "");
  const n = parseFloat(cleaned);
  if (!isFinite(n)) return null;
  return isNeg ? -n : n;
}
function detectColType(samples: string[]): "number" | "date" | "string" {
  const real = samples.filter(s => s != null && String(s).trim() !== "" && String(s).trim() !== "—");
  if (!real.length) return "string";
  if (real.every(s => /^\d{4}-\d{2}-\d{2}(\b|$)/.test(String(s).trim()))) return "date";
  if (real.every(s => parseSortNum(s) !== null)) return "number";
  return "string";
}

function applyTableSort(tableEl: HTMLTableElement, section: string): void {
  const sort = loadSort(section);
  if (!sort) return;
  const headThs = Array.from(tableEl.querySelectorAll<HTMLTableCellElement>(":scope > thead > tr > th"));
  let colIndex = headThs.findIndex(th => (th.getAttribute("data-col") || "") === sort.key);
  if (colIndex < 0 && sort.key.startsWith("#")) colIndex = Number(sort.key.slice(1));
  if (!Number.isFinite(colIndex) || colIndex < 0 || colIndex >= headThs.length) return;
  const tbody = tableEl.querySelector<HTMLTableSectionElement>(":scope > tbody");
  if (!tbody) return;
  const rows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>(":scope > tr"));
  if (rows.length < 2) return;
  if (rows.length === 1 && rows[0].children.length === 1 && rows[0].children[0].hasAttribute("colspan")) return;

  const getCell = (row: HTMLTableRowElement) => row.children[colIndex] as HTMLElement | undefined;
  const getText = (row: HTMLTableRowElement) => { const c = getCell(row); return c ? (c.textContent || "").trim() : ""; };

  const samples = rows.slice(0, 20).map(getText);
  const colType = detectColType(samples);

  const cmp = (a: HTMLTableRowElement, b: HTMLTableRowElement) => {
    const av = getText(a), bv = getText(b);
    if (colType === "number") {
      const ax = parseSortNum(av), bx = parseSortNum(bv);
      if (ax === null && bx === null) return 0;
      if (ax === null) return 1;
      if (bx === null) return -1;
      return ax - bx;
    }
    if (colType === "date") {
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return av.localeCompare(bv);
    }
    return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
  };

  rows.sort(cmp);
  if (sort.dir === "desc") rows.reverse();
  rows.forEach(r => tbody.appendChild(r));
}

function refreshSortIndicators(tableEl: HTMLTableElement, section: string): void {
  const sort = loadSort(section);
  const ths = tableEl.querySelectorAll<HTMLTableCellElement>(":scope > thead > tr > th");
  ths.forEach((th, i) => {
    const ind = th.querySelector<HTMLSpanElement>(".sort-indicator");
    if (!ind) return;
    const key = th.getAttribute("data-col") || `#${i}`;
    if (sort && key === sort.key) {
      ind.textContent = sort.dir === "asc" ? "▲" : "▼";
      ind.style.opacity = "1";
      ind.style.color = "#a5b4fc";
    } else {
      ind.textContent = "";
      ind.style.opacity = ".55";
      ind.style.color = "";
    }
  });
}

function attachSortHandlers(tableEl: HTMLTableElement, section: string): void {
  const ths = Array.from(tableEl.querySelectorAll<HTMLTableCellElement>(":scope > thead > tr > th"));
  ths.forEach((th, i) => {
    // Tables without data-col headers (rips, sales, purchases, …) sort by
    // positional key "#<index>" — restores the pre-v2 behavior for them.
    const txt = (th.textContent || "").trim();
    const key = th.getAttribute("data-col") || (txt ? `#${i}` : "");
    if (!key) return;
    if (!th.querySelector(".sort-indicator")) {
      const ind = document.createElement("span");
      ind.className = "sort-indicator";
      ind.style.cssText = "margin-left:.3em;opacity:.55;font-size:.85em;font-weight:600";
      th.appendChild(ind);
    }
    th.setAttribute("data-sortable", "1");
    th.style.cursor = "pointer";
    th.style.userSelect = "none";
    th.onclick = () => {
      const cur = loadSort(section);
      let next: SortRecord | null;
      if (cur && cur.key === key) {
        next = cur.dir === "asc" ? { key, dir: "desc" } : null;
      } else {
        next = { key, dir: "asc" };
      }
      saveSort(section, next);
      // Always re-render: data-sorted sections re-sort their full dataset in
      // the view; others get the DOM page sort reapplied by setupAllTables.
      (window as any).render?.();
    };
  });
  refreshSortIndicators(tableEl, section);
}

/* ------------------------------------------------------------------ */
/*  Public entry point                                                  */
/* ------------------------------------------------------------------ */
export function setupAllTables(root: HTMLElement, section: string): void {
  const tables = root.querySelectorAll<HTMLTableElement>("table.t");
  tables.forEach((tbl) => {
    applyHiddenColumns(tbl, section);
    applyCellTitles(tbl);
    const panel = tbl.parentElement;
    const isMain =
      panel?.classList.contains("panel") &&
      panel?.classList.contains("scrollx") &&
      panel?.parentElement?.id === "view";
    if (isMain) {
      attachSortHandlers(tbl, section);
      if (!DATA_SORTED_SECTIONS.has(section)) applyTableSort(tbl, section);
      injectColumnsControl(tbl, section);
    }
  });
}
